import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Loader2,
  Sun,
  Moon,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

const Index = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1E]">
        <Loader2 className="h-7 w-7 animate-spin text-amber-400" />
      </div>
    );
  }

  const mockBarData = [42, 58, 48, 72, 64, 88, 78];
  const mockPieData = [
    { label: 'Alimentação', percent: 35, color: 'hsl(38 88% 52%)' },
    { label: 'Transporte', percent: 25, color: 'hsl(158 55% 42%)' },
    { label: 'Compras', percent: 22, color: 'hsl(262 58% 62%)' },
    { label: 'Contas', percent: 18, color: 'hsl(200 72% 48%)' },
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* ── Navigation ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#070C18]/80 backdrop-blur-md border-b border-white/6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-md gradient-primary flex items-center justify-center">
              <span className="text-[hsl(220_40%_8%)] font-bold text-xs font-display">P</span>
            </div>
            <span className="text-white font-semibold text-sm tracking-tight">Patri</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-white/40 hover:text-white hover:bg-white/8 transition-all"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="flex items-center gap-1.5 px-4 py-2 rounded-md gradient-primary text-[hsl(220_40%_8%)] text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Acessar Painel
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-md text-white/60 hover:text-white text-sm transition-colors"
                >
                  Entrar
                </Link>
                <Link
                  to="/signup"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md gradient-primary text-[hsl(220_40%_8%)] text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  Criar Conta
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── Hero — always dark ── */}
      <section className="relative pt-28 pb-24 px-4 sm:px-6 bg-[#070C18] hero-grid-bg overflow-hidden">
        {/* Ambient glow orbs */}
        <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full bg-amber-500/8 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-blue-600/8 blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          {/* Label pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/12 border border-amber-500/20 mb-8 animate-fade-in">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-amber-400/90 text-xs font-medium tracking-wide">Controle financeiro pessoal</span>
          </div>

          <h1 className="font-display text-3xl sm:text-5xl md:text-7xl font-bold text-white leading-[1.05] mb-6 animate-fade-up stagger-1">
            Gerencie seu
            <br />
            <span className="text-primary">
              dinheiro
            </span>{' '}
            com clareza
          </h1>

          <p className="text-white/50 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-up stagger-2">
            Acompanhe receitas e despesas, organize por categorias e visualize seus
            padrões financeiros — sem complicação.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-14 animate-fade-up stagger-3">
            {!isAuthenticated ? (
              <>
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg gradient-primary text-[hsl(220_40%_8%)] text-sm font-semibold hover:opacity-90 transition-opacity glow-amber"
                >
                  Começar Grátis
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg border border-white/14 text-white/70 text-sm font-medium hover:bg-white/6 hover:text-white transition-all"
                >
                  Já tenho conta
                </Link>
              </>
            ) : (
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg gradient-primary text-[hsl(220_40%_8%)] text-sm font-semibold hover:opacity-90 transition-opacity glow-amber"
              >
                Acessar Painel
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 animate-fade-up stagger-4">
            {[
              'Sem planilha, sem banco',
              'Gratuito. Sem plano pago.',
              'Lançamento em segundos',
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2 text-white/35 text-sm">
                <CheckCircle2 className="h-3.5 w-3.5 text-amber-400/60 shrink-0" />
                {text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── App Preview ── */}
      <section className="py-20 px-6 bg-background">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
              Tudo numa visão só
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Gráficos e relatórios que ajudam você a entender para onde vai seu dinheiro.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Bar chart mock */}
            <div className="glass rounded-xl p-5 animate-scale-in card-accent-primary">
              <p className="text-xs font-medium text-muted-foreground mb-1">Gastos da Semana</p>
              <p className="text-lg font-bold text-foreground mb-4">R$ 1.840</p>
              <div className="flex items-end justify-between h-28 gap-1.5">
                {mockBarData.map((h, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full rounded-sm transition-all"
                      style={{ height: `${h}%`, background: 'linear-gradient(180deg, hsl(38 88% 52%), hsl(28 88% 54%))' }}
                    />
                    <span className="text-[9px] text-muted-foreground">
                      {['S', 'T', 'Q', 'Q', 'S', 'S', 'D'][i]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pie chart mock */}
            <div className="glass rounded-xl p-5 animate-scale-in card-accent-income" style={{ animationDelay: '80ms' }}>
              <p className="text-xs font-medium text-muted-foreground mb-1">Por Categoria</p>
              <p className="text-lg font-bold text-foreground mb-4">4 categorias</p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="relative w-20 h-20 shrink-0">
                  <svg viewBox="0 0 100 100" className="transform -rotate-90 w-full h-full">
                    {mockPieData.reduce<{ elements: React.ReactNode[]; offset: number }>(
                      (acc, item, i) => {
                        const circumference = 2 * Math.PI * 38;
                        const dash = (item.percent / 100) * circumference;
                        acc.elements.push(
                          <circle
                            key={i}
                            cx="50" cy="50" r="38"
                            fill="none"
                            stroke={item.color}
                            strokeWidth="18"
                            strokeDasharray={`${dash} ${circumference}`}
                            strokeDashoffset={-acc.offset}
                          />
                        );
                        acc.offset += dash;
                        return acc;
                      },
                      { elements: [], offset: 0 }
                    ).elements}
                  </svg>
                </div>
                <div className="space-y-1.5 flex-1 min-w-0">
                  {mockPieData.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-muted-foreground truncate">{item.label}</span>
                      <span className="text-xs font-semibold text-foreground ml-auto">{item.percent}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Summary mock — mirrors the app's Semantic Anchor Rule:
                numbers are neutral, semantic color is anchored in the bar (chart-fill, allowed). */}
            <div className="glass rounded-xl p-5 animate-scale-in md:col-span-2 lg:col-span-1" style={{ animationDelay: '160ms' }}>
              <p className="text-xs font-medium text-muted-foreground mb-4">Resumo do Mês</p>
              <div className="space-y-3">
                {[
                  { label: 'Receitas', value: 'R$ 4.200', dot: 'bg-income', bar: 85, barColor: 'bg-income' },
                  { label: 'Despesas', value: 'R$ 2.840', dot: 'bg-expense', bar: 57, barColor: 'bg-expense' },
                  { label: 'Saldo', value: 'R$ 1.360', dot: 'bg-primary', bar: 100, barColor: 'gradient-primary' },
                ].map((row, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${row.dot}`} aria-hidden="true" />
                        {row.label}
                      </span>
                      <span className="text-sm font-bold text-foreground tabular-nums">{row.value}</span>
                    </div>
                    <div className="h-1 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${row.barColor}`}
                        style={{ width: `${row.bar}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 px-6 bg-background border-t border-border/40">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-12">
            Por que o Patri
          </p>

          <div className="divide-y divide-border/50">
            <div className="py-10 grid grid-cols-[3rem_1fr] sm:grid-cols-[4.5rem_1fr] gap-6 items-start">
              <span className="font-display font-bold text-foreground/10 text-5xl leading-none select-none">01</span>
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground mb-3 tracking-tight">
                  Lançamentos em lote
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-lg">
                  Registre 10 transações em dois minutos. Sem formulário por formulário, sem interrupções — tudo inline, tudo de uma vez.
                </p>
              </div>
            </div>

            <div className="py-10 grid grid-cols-[3rem_1fr] sm:grid-cols-[4.5rem_1fr] gap-6 items-start">
              <span className="font-display font-bold text-foreground/10 text-5xl leading-none select-none">02</span>
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground mb-3 tracking-tight">
                  Categorias do seu jeito
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-lg">
                  Crie categorias que fazem sentido para a sua vida. Cada receita e cada gasto com contexto.
                </p>
              </div>
            </div>

            <div className="py-10 grid grid-cols-[3rem_1fr] sm:grid-cols-[4.5rem_1fr] gap-6 items-start">
              <span className="font-display font-bold text-foreground/10 text-5xl leading-none select-none">03</span>
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground mb-3 tracking-tight">
                  Relatórios que respondem perguntas
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-lg">
                  Qualquer mês, qualquer intervalo. Veja para onde foi seu dinheiro antes de sentir que fugiu do controle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 bg-[#070C18] relative overflow-hidden">
        <div className="absolute inset-0 hero-grid-bg" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-amber-500/6 blur-3xl pointer-events-none" />

        <div className="max-w-2xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6">
            <CheckCircle2 className="h-4 w-4 text-amber-400" />
            <span className="text-amber-400/80 text-sm">Gratuito para sempre</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Pronto para assumir o controle?
          </h2>
          <p className="text-white/45 text-lg mb-8 leading-relaxed">
            Crie sua conta e comece a enxergar suas finanças com mais clareza.
          </p>
          {!isAuthenticated ? (
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg gradient-primary text-[hsl(220_40%_8%)] font-semibold hover:opacity-90 transition-opacity glow-amber"
            >
              Criar Conta Grátis
              <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg gradient-primary text-[hsl(220_40%_8%)] font-semibold hover:opacity-90 transition-opacity glow-amber"
            >
              Acessar Painel
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-[#070C18] border-t border-white/6 py-6 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 rounded gradient-primary flex items-center justify-center">
              <span className="text-[hsl(220_40%_8%)] font-bold text-[9px]">P</span>
            </div>
            <span className="text-white/30 text-xs">Patri</span>
          </div>
          <p className="text-white/25 text-xs">© {new Date().getFullYear()} Patri. Finanças pessoais.</p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
